/**
 * 目标设置页面（合并版）
 * 展示部门目标 + 人员目标，人员目标汇总为部门总目标
 */

import React, { useState, useEffect, useCallback } from 'react';
import { message, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { kpiSettingsAPI, DepartmentKPITarget, KPIDimension, PerformancePeriod, Department, DepartmentKPIProfile } from '../../../services/kpiSettingsService';
import api from '../../../services/api/client';
import { TargetFilterBar } from './TargetSettingsComponents/TargetFilterBar';
import { DeptTargetTable } from './TargetSettingsComponents/DeptTargetTable';

const { Text } = Typography;

export interface MemberInfo {
    id: number;
    user_id: number;
    username: string;
    full_name: string;
    department: number;
}

const TargetSettings: React.FC = () => {
    // 数据状态
    const [departments, setDepartments] = useState<Department[]>([]);
    const [periods, setPeriods] = useState<PerformancePeriod[]>([]);
    const [dimensions, setDimensions] = useState<KPIDimension[]>([]);
    const [profiles, setProfiles] = useState<DepartmentKPIProfile[]>([]);
    const [members, setMembers] = useState<MemberInfo[]>([]);

    // 选择状态
    const [selectedPeriodId, setSelectedPeriodId] = useState<number>();
    const [selectedDeptId, setSelectedDeptId] = useState<number>();

    // 编辑状态
    const [editingDeptTargets, setEditingDeptTargets] = useState<Record<string, number>>({});
    const [editingMemberTargets, setEditingMemberTargets] = useState<Record<string, number>>({});

    // UI 状态
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // 加载基础数据
    const loadBaseData = useCallback(async () => {
        try {
            const [deptRes, periodRes, dimRes] = await Promise.all([
                kpiSettingsAPI.getDepartments(), kpiSettingsAPI.getPeriods(), kpiSettingsAPI.getDimensions(),
            ]);
            // 部门数据
            const deptData = (deptRes.data as any)?.results || deptRes.data || [];
            setDepartments(Array.isArray(deptData) ? deptData : []);
            // 周期数据
            const periodsData = (periodRes.data as any)?.results || periodRes.data || [];
            setPeriods(Array.isArray(periodsData) ? periodsData : []);
            if (Array.isArray(periodsData) && periodsData.length > 0 && !selectedPeriodId) {
                setSelectedPeriodId(periodsData[0].id);
            }
            // 指标数据
            const dimData = (dimRes.data as any)?.results || dimRes.data || [];
            setDimensions(Array.isArray(dimData) ? dimData : []);
        } catch { message.error('加载数据失败'); }
    }, [selectedPeriodId]);

    // 加载目标数据
    const loadAllData = useCallback(async () => {
        if (!selectedPeriodId) return;
        setLoading(true);
        try {
            const profileRes = await kpiSettingsAPI.getProfilesByPeriod(selectedPeriodId);
            setProfiles(profileRes.data);

            const memberParams: any = { page_size: 1000 };
            if (selectedDeptId) memberParams.department_id = selectedDeptId;
            const memberRes = await api.get('/members/', { params: memberParams });
            const membersData = (memberRes.data as any)?.results || memberRes.data || [];
            setMembers(Array.isArray(membersData) ? membersData : []);

            const targetParams: any = { period_id: selectedPeriodId };
            if (selectedDeptId) targetParams.department_id = selectedDeptId;
            const deptTargetRes = await kpiSettingsAPI.getTargets(selectedDeptId, selectedPeriodId);
            const deptTargetsData = (deptTargetRes.data as any)?.results || deptTargetRes.data || [];

            const newDeptEditing: Record<string, number> = {};
            (Array.isArray(deptTargetsData) ? deptTargetsData : []).forEach((t: any) => {
                newDeptEditing[`${t.department}-${t.dimension}`] = parseFloat(t.target_value);
            });
            setEditingDeptTargets(newDeptEditing);

            const memberTargetParams: any = { period_id: selectedPeriodId };
            const memberTargetRes = await api.get('/member-kpi-targets/', { params: memberTargetParams });
            const memberTargetsData = (memberTargetRes.data as any)?.results || memberTargetRes.data || [];

            const newMemberEditing: Record<string, number> = {};
            (Array.isArray(memberTargetsData) ? memberTargetsData : []).forEach((t: any) => {
                newMemberEditing[`${t.member}-${t.dimension}`] = parseFloat(t.target_value);
            });
            setEditingMemberTargets(newMemberEditing);

        } catch (error) { console.error(error); message.error('加载数据失败'); }
        finally { setLoading(false); }
    }, [selectedPeriodId, selectedDeptId]);

    useEffect(() => { loadBaseData(); }, [loadBaseData]);
    useEffect(() => { if (selectedPeriodId) loadAllData(); }, [selectedPeriodId, selectedDeptId, loadAllData]);

    const getDeptKPIs = (deptId: number): KPIDimension[] => {
        const profile = profiles.find(p => p.department === deptId);
        if (!profile) return [];
        const kpiIds = [profile.kpi1, profile.kpi2, profile.kpi3, profile.kpi4].filter(Boolean);
        return dimensions.filter(d => kpiIds.includes(d.id));
    };

    const getMemberTargetSum = (deptId: number, dimensionId: number): number => {
        const deptMembers = members.filter(m => m.department === deptId);
        let sum = 0;
        deptMembers.forEach(m => sum += editingMemberTargets[`${m.id}-${dimensionId}`] || 0);
        return sum;
    };

    const handleDeptTargetChange = (deptId: number, dimId: number, val: number | null) => {
        setEditingDeptTargets(prev => ({ ...prev, [`${deptId}-${dimId}`]: val || 0 }));
    };

    const handleMemberTargetChange = (memberId: number, dimId: number, val: number | null) => {
        setEditingMemberTargets(prev => ({ ...prev, [`${memberId}-${dimId}`]: val || 0 }));
    };

    const handleSave = async () => {
        if (!selectedPeriodId) return message.warning('请先选择周期');
        setSaving(true);
        try {
            const deptsToSave = selectedDeptId
                ? [selectedDeptId]
                : departments.filter(d => profiles.some(p => p.department === d.id)).map(d => d.id);

            for (const deptId of deptsToSave) {
                const kpis = getDeptKPIs(deptId);
                const targets = kpis.map(kpi => ({
                    dimension_id: kpi.id, target_value: editingDeptTargets[`${deptId}-${kpi.id}`] || 0
                })).filter(t => t.target_value !== undefined);
                if (targets.length > 0) await kpiSettingsAPI.batchSaveTargets(deptId, selectedPeriodId, targets);
            }

            const memberTargetsPayload: any[] = [];
            members.forEach(m => {
                if (deptsToSave.includes(m.department)) {
                    getDeptKPIs(m.department).forEach(kpi => {
                        const val = editingMemberTargets[`${m.id}-${kpi.id}`];
                        if (val !== undefined) {
                            memberTargetsPayload.push({ member_id: m.id, dimension_id: kpi.id, target_value: val });
                        }
                    });
                }
            });

            if (memberTargetsPayload.length > 0) {
                await api.post('/member-kpi-targets/batch_update/', { period_id: selectedPeriodId, targets: memberTargetsPayload });
            }
            message.success('保存成功');
            loadAllData();
        } catch { message.error('保存失败'); }
        finally { setSaving(false); }
    };

    const deptsToShow = selectedDeptId ? departments.filter(d => d.id === selectedDeptId) : departments;

    return (
        <div style={{ padding: 24 }}>
            <TargetFilterBar
                periods={periods} departments={departments}
                selectedPeriodId={selectedPeriodId} selectedDeptId={selectedDeptId}
                onPeriodChange={setSelectedPeriodId} onDeptChange={setSelectedDeptId}
                onRefresh={loadAllData} onSave={handleSave} saving={saving}
            />

            <div style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 16, color: '#666' }}>
                    <InfoCircleOutlined style={{ marginRight: 8 }} />
                    <Text type="secondary">人员目标汇总 = 部门成员目标之和。支持差异化目标设定。</Text>
                </div>
            </div>

            {loading ? <div style={{ textAlign: 'center', padding: 50 }}>加载中...</div> :
                deptsToShow.length === 0 ? <div style={{ textAlign: 'center', color: '#999', padding: 50 }}>暂无部门数据</div> : (
                    deptsToShow.map(dept => (
                        <DeptTargetTable
                            key={dept.id} dept={dept} kpis={getDeptKPIs(dept.id)}
                            deptMembers={members.filter(m => m.department === dept.id)}
                            editingDeptTargets={editingDeptTargets} editingMemberTargets={editingMemberTargets}
                            onDeptTargetChange={handleDeptTargetChange} onMemberTargetChange={handleMemberTargetChange}
                            getMemberTargetSum={getMemberTargetSum}
                        />
                    ))
                )}
        </div>
    );
};

export default TargetSettings;
